import React, { useState, useEffect } from 'react';
import { buildApiUrl, API_ENDPOINTS } from '../utils/api';

interface Config {
  llm: {
    provider: string;
    moonshot: {
      api_key: string;
      model: string;
    };
    aliyun_deepseek: {
      api_key: string;
      model: string;
      base_url: string;
      enable_thinking: boolean;
    };
  };
  search: {
    provider: string;
    bocha: {
      api_key: string;
    };
  };
  timeout: {
    request_timeout: number;
    llm_request_timeout: number;
    max_retries: number;
  };
}

const AdminPanel: React.FC = () => {
  const [config, setConfig] = useState<Config | null>(null);
  const [prompt, setPrompt] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'config' | 'prompt'>('config');

  useEffect(() => {
    loadConfig();
    loadPrompt();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_CONFIG));
      const data = await response.json();
      setConfig(data);
    } catch (error) {
      console.error('加载配置失败:', error);
      setMessage({ type: 'error', text: '加载配置失败' });
    }
  };

  const loadPrompt = async () => {
    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_PROMPT));
      const data = await response.json();
      setPrompt(data.prompt || '');
    } catch (error) {
      console.error('加载提示词失败:', error);
      setMessage({ type: 'error', text: '加载提示词失败' });
    }
  };

  const saveConfig = async () => {
    if (!config) return;
    
    setLoading(true);
    setMessage(null);
    
    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_CONFIG), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: '配置已保存！部分配置需要重启服务才能生效。' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || '保存配置失败' });
      }
    } catch (error) {
      console.error('保存配置失败:', error);
      setMessage({ type: 'error', text: '保存配置失败' });
    } finally {
      setLoading(false);
    }
  };

  const savePrompt = async () => {
    setLoading(true);
    setMessage(null);
    
    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_PROMPT), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt }),
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: '提示词已保存！' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || '保存提示词失败' });
      }
    } catch (error) {
      console.error('保存提示词失败:', error);
      setMessage({ type: 'error', text: '保存提示词失败' });
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = (path: string[], value: any) => {
    if (!config) return;
    
    const newConfig = { ...config };
    let current: any = newConfig;
    
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    
    current[path[path.length - 1]] = value;
    setConfig(newConfig);
  };

  if (!config) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">加载中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">系统管理后台</h1>
          
          {/* 标签页 */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('config')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'config'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                配置管理
              </button>
              <button
                onClick={() => setActiveTab('prompt')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'prompt'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                提示词管理
              </button>
            </nav>
          </div>

          {/* 消息提示 */}
          {message && (
            <div
              className={`mb-4 p-4 rounded-lg ${
                message.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {message.text}
            </div>
          )}

          {/* 配置管理标签页 */}
          {activeTab === 'config' && (
            <div className="space-y-6">
              {/* LLM 配置 */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h2 className="text-xl font-semibold text-gray-700 mb-4">大模型配置</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      提供商
                    </label>
                    <select
                      value={config.llm.provider}
                      onChange={(e) => updateConfig(['llm', 'provider'], e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="moonshot">月之暗面 (Moonshot)</option>
                      <option value="aliyun-deepseek">阿里云 DeepSeek</option>
                    </select>
                  </div>

                  {/* Moonshot 配置 */}
                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-lg font-medium text-gray-700 mb-3">月之暗面配置</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          API Key
                        </label>
                        <input
                          type="password"
                          value={config.llm.moonshot.api_key}
                          onChange={(e) => updateConfig(['llm', 'moonshot', 'api_key'], e.target.value)}
                          placeholder="sk-..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          模型
                        </label>
                        <input
                          type="text"
                          value={config.llm.moonshot.model}
                          onChange={(e) => updateConfig(['llm', 'moonshot', 'model'], e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* DeepSeek 配置 */}
                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-lg font-medium text-gray-700 mb-3">阿里云 DeepSeek 配置</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          API Key
                        </label>
                        <input
                          type="password"
                          value={config.llm.aliyun_deepseek.api_key}
                          onChange={(e) => updateConfig(['llm', 'aliyun_deepseek', 'api_key'], e.target.value)}
                          placeholder="sk-..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          模型
                        </label>
                        <input
                          type="text"
                          value={config.llm.aliyun_deepseek.model}
                          onChange={(e) => updateConfig(['llm', 'aliyun_deepseek', 'model'], e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Base URL
                        </label>
                        <input
                          type="text"
                          value={config.llm.aliyun_deepseek.base_url}
                          onChange={(e) => updateConfig(['llm', 'aliyun_deepseek', 'base_url'], e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={config.llm.aliyun_deepseek.enable_thinking}
                          onChange={(e) => updateConfig(['llm', 'aliyun_deepseek', 'enable_thinking'], e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-700">
                          启用 Thinking 模式
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 搜索配置 */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h2 className="text-xl font-semibold text-gray-700 mb-4">搜索配置</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      提供商
                    </label>
                    <select
                      value={config.search.provider}
                      onChange={(e) => updateConfig(['search', 'provider'], e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="bocha">博查搜索</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      博查 API Key
                    </label>
                    <input
                      type="password"
                      value={config.search.bocha.api_key}
                      onChange={(e) => updateConfig(['search', 'bocha', 'api_key'], e.target.value)}
                      placeholder="sk-..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* 超时配置 */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h2 className="text-xl font-semibold text-gray-700 mb-4">超时配置</h2>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      请求超时（秒）
                    </label>
                    <input
                      type="number"
                      value={config.timeout.request_timeout}
                      onChange={(e) => updateConfig(['timeout', 'request_timeout'], parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      LLM 请求超时（秒）
                    </label>
                    <input
                      type="number"
                      value={config.timeout.llm_request_timeout}
                      onChange={(e) => updateConfig(['timeout', 'llm_request_timeout'], parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      最大重试次数
                    </label>
                    <input
                      type="number"
                      value={config.timeout.max_retries}
                      onChange={(e) => updateConfig(['timeout', 'max_retries'], parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <button
                onClick={saveConfig}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '保存中...' : '保存配置'}
              </button>
            </div>
          )}

          {/* 提示词管理标签页 */}
          {activeTab === 'prompt' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  提示词模板
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  使用 {"{variable}"} 和 {"{asset}"} 作为变量占位符，{"{search_context}"} 作为搜索结果占位符
                </p>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={30}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="请输入提示词模板..."
                />
              </div>
              <button
                onClick={savePrompt}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '保存中...' : '保存提示词'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;


